/**
 * TCPN PHASE S1-E — Before/After Proof Chip + Viewer
 * ====================================================
 * Compact TRUST-LAYER signal for the public search card and provider profile.
 * Renders ONLY when the backend resolves `has_before_after_proof === true`
 * (predicate enforced server-side in `services/public_ba_proof_service.py`,
 * mirrors the existing public batch badges endpoint).
 *
 * Tap/click opens a small modal viewer that fetches the public-safe case
 * payload from /api/public/clinics/{cid}/before-after-cases. No PII. No
 * route transition.
 *
 * Density rules (per S2-B3 Section C.2):
 *   - Card chip survives chip-strip collapse — only the campaign chip has
 *     higher priority.
 *   - Chip text is "Before/after proof" on every viewport (S1-E.1 copy
 *     cleanup — the internal "BA" abbreviation is never patient-facing).
 *
 * Locked patient-facing copy:
 *   chip            : "Before/after proof"
 *   tooltip         : "Patient consent obtained. Images reviewed before publication."
 *   viewer subtitle : "Consent-backed before/after"
 *   viewer footer   : "Patient consent on record. Images moderated before
 *                      publication. No personally identifying details displayed."
 */
import React, { useEffect, useState } from 'react';
import { formatDateLocale } from '../utils/dateFormat';
import { Sparkles, X } from 'lucide-react';

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CHIP_LABEL = 'Before/after proof';
const TOOLTIP_TEXT = 'Patient consent obtained. Images reviewed before publication.';
const VIEWER_SUBTITLE = 'Consent-backed before/after';
const VIEWER_FOOTER = 'Patient consent on record. Images moderated before publication. No personally identifying details displayed.';

function _abs(url) {
  if (!url) return null;
  if (/^https?:/.test(url)) return url;
  const base = (REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
  return `${base}${url}`;
}

/**
 * <BeforeAfterProofChip>
 *   clinicId       : provider id whose proof to fetch on tap
 *   clinicName     : friendly name (used as viewer header)
 *   treatmentSlug  : when set, viewer scopes to this treatment slug only
 *   testid         : data-testid prefix (default "ba-proof")
 *
 * The chip itself does NOT fetch — the viewer modal does, and only on tap.
 * S1-E.1 copy lock: chip text is the same on every viewport
 * ("Before/after proof"). The internal "BA" abbreviation is reserved for
 * code/API/test identifiers; it never reaches a patient surface.
 */
export default function BeforeAfterProofChip({
  clinicId,
  clinicName,
  treatmentSlug,
  testid = 'ba-proof',
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title={TOOLTIP_TEXT}
        aria-label={`${CHIP_LABEL}. ${TOOLTIP_TEXT}`}
        className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors flex-shrink-0 whitespace-nowrap"
        data-testid={`${testid}-chip`}
      >
        <Sparkles className="h-2.5 w-2.5" />
        {CHIP_LABEL}
      </button>
      {open && (
        <BeforeAfterProofViewer
          clinicId={clinicId}
          clinicName={clinicName}
          treatmentSlug={treatmentSlug}
          onClose={() => setOpen(false)}
          testid={testid}
        />
      )}
    </>
  );
}

function BeforeAfterProofViewer({ clinicId, clinicName, treatmentSlug, onClose, testid }) {
  const [loading, setLoading] = useState(true);
  const [cases, setCases] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    const ctrl = new AbortController();
    let qs = '';
    if (treatmentSlug) qs = `?treatment_slug=${encodeURIComponent(treatmentSlug)}`;
    const url = `${REACT_APP_BACKEND_URL}/api/public/clinics/${encodeURIComponent(clinicId)}/before-after-cases${qs}`;
    fetch(url, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((j) => {
        if (j && Array.isArray(j.cases)) setCases(j.cases);
        else setError('Could not load proof.');
      })
      .catch((e) => { if (e.name !== 'AbortError') setError('Could not load proof.'); })
      .finally(() => setLoading(false));
    return () => ctrl.abort();
  }, [clinicId, treatmentSlug]);

  // Esc key + click outside close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testid}-viewer-title`}
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60"
      onClick={onClose}
      data-testid={`${testid}-viewer`}
    >
      <div
        className="relative w-full max-w-lg max-h-[90vh] overflow-auto bg-white rounded-lg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 px-4 py-3 border-b bg-white">
          <div className="min-w-0">
            <div className="text-xs font-semibold text-emerald-700 uppercase tracking-wider">Before / After Proof</div>
            <h3 id={`${testid}-viewer-title`} className="text-base font-semibold text-slate-800 truncate">
              {clinicName || 'Clinic'}
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">{VIEWER_SUBTITLE}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 rounded hover:bg-slate-100 text-slate-500 flex-shrink-0"
            data-testid={`${testid}-viewer-close`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {loading && (
            <div className="text-xs text-slate-400 text-center py-6" data-testid={`${testid}-viewer-loading`}>
              Loading consent-backed proof…
            </div>
          )}
          {!loading && error && (
            <div className="text-xs text-red-600 text-center py-6" data-testid={`${testid}-viewer-error`}>
              {error}
            </div>
          )}
          {!loading && !error && cases.length === 0 && (
            <div className="text-xs text-slate-400 text-center py-6" data-testid={`${testid}-viewer-empty`}>
              No published proof for this filter.
            </div>
          )}
          {!loading && !error && cases.map((c) => (
            <div
              key={c.case_id}
              className="border rounded-md p-3 bg-slate-50/40"
              data-testid={`${testid}-viewer-case-${c.case_id}`}
            >
              <div className="grid grid-cols-2 gap-3">
                {[['Before', c.before_thumbnail_url, c.before_preview_url],
                  ['After',  c.after_thumbnail_url,  c.after_preview_url]].map(([label, thumb, preview]) => (
                  <figure key={label} className="flex flex-col items-center">
                    <div className="w-full aspect-square overflow-hidden rounded bg-white border border-slate-200 flex items-center justify-center">
                      <img
                        src={_abs(preview || thumb)}
                        alt={`${label} treatment image`}
                        loading="lazy"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <figcaption className="mt-1 text-[10px] uppercase tracking-wider text-slate-500">{label}</figcaption>
                  </figure>
                ))}
              </div>
              <div className="mt-3 flex items-center justify-between gap-3 text-xs">
                <span className="font-medium text-slate-700">{c.treatment_slug}</span>
                {c.approved_at && (
                  <time className="text-[10px] text-slate-400" dateTime={c.approved_at}>
                    {formatDateLocale(c.approved_at)}
                  </time>
                )}
              </div>
              {c.caption && <p className="mt-2 text-xs text-slate-600 leading-snug">{c.caption}</p>}
            </div>
          ))}
        </div>

        <div className="px-4 py-2.5 border-t bg-slate-50/60 text-[10px] text-slate-500 leading-relaxed">
          {VIEWER_FOOTER}
        </div>
      </div>
    </div>
  );
}
